import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {
  ThemeProvider,
  Grid,
  SectionIntro,
  Box,
  Text,
  Heading,
  Button,
  UnorderedList,
  Stack,
  OrderedList,
} from '@primer/react-brand'

export function CopilotPlansIndex() {
  const {copilotSignupPath} = useRoutePayload<{copilotSignupPath: string}>()
  const {copilotForBusinessSignupPath} = useRoutePayload<{copilotForBusinessSignupPath: string}>()
  const {copilotContactSalesPath} = useRoutePayload<{copilotContactSalesPath: string}>()

  return (
    <>
      <ThemeProvider colorMode="dark" className="lp-Copilot">
        <section id="pricing" className="lp-Section lp-Section--pricing">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro className="lp-SectionIntro" align="center">
                <SectionIntro.Heading size="1" weight="bold">
                  Take flight with the best Copilot plan for you
                </SectionIntro.Heading>
                <SectionIntro.Description>
                  Whether you’re an organization or individual, GitHub Copilot helps you build better software, faster.
                </SectionIntro.Description>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <Heading as="h3" size="5" className="lp-Pricing-groupTitle">
                For organizations
              </Heading>

              <Grid className="lp-Pricing">
                <Grid.Column span={12} className="lp-Pricing-item has-BlurredBg has-GradientBorder">
                  <Stack
                    direction={{narrow: 'vertical', regular: 'horizontal'}}
                    gap="none"
                    padding="none"
                    className="lp-Pricing--business"
                  >
                    {/* Copilot */}
                    <Stack padding="none" gap={24} style={{flex: '1'}}>
                      <Box>
                        <Text size="100" weight="semibold" className="lp-Label--pricing">
                          Recommended
                        </Text>
                        <Box marginBlockStart={8} marginBlockEnd={16}>
                          <Heading as="h4" size="5">
                            Copilot Business
                          </Heading>
                        </Box>
                        <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                          Copilot in the coding environment.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                        <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          19
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per user / month
                        </Text>
                      </Stack>

                      <hr className="lp-Pricing-divider" />

                      <UnorderedList variant="checked" className="lp-Pricing-list">
                        <UnorderedList.Item>Code completions</UnorderedList.Item>
                        <UnorderedList.Item>
                          Chat in IDE <sup>1</sup> and Mobile <sup>2</sup>
                        </UnorderedList.Item>
                        <UnorderedList.Item>
                          CLI assistance <sup>3</sup>
                        </UnorderedList.Item>
                        <UnorderedList.Item>Security vulnerability filter</UnorderedList.Item>
                        <UnorderedList.Item>Code referencing</UnorderedList.Item>
                        <UnorderedList.Item>Public code filter</UnorderedList.Item>
                        <UnorderedList.Item>IP indemnity</UnorderedList.Item>
                        <UnorderedList.Item>Enterprise-grade security, safety, and privacy</UnorderedList.Item>
                      </UnorderedList>

                      <Stack direction={{narrow: 'vertical', wide: 'horizontal'}} gap={12} padding="none">
                        <Button as="a" href={copilotForBusinessSignupPath} block variant="primary">
                          Buy now
                        </Button>
                        <Button as="a" href={copilotContactSalesPath} block className="lp-Button--onColoredBg">
                          Contact sales
                        </Button>
                      </Stack>
                    </Stack>

                    <hr className="lp-Pricing-divider lp-Pricing-divider--business" />

                    {/* Copilot Enterprise */}
                    <Stack padding="none" gap={24} style={{flex: '1'}}>
                      <Box>
                        <Text size="100" weight="semibold" className="lp-Label--pricing">
                          Available Feb 2024
                        </Text>
                        <Box marginBlockStart={8} marginBlockEnd={16}>
                          <Heading as="h4" size="5">
                            Copilot Enterprise
                          </Heading>
                        </Box>
                        <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                          Copilot personalized to your organization throughout the software development lifecycle.
                          Requires GitHub Enterprise Cloud.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                        <Text size="500" weight="normal" className="is-sansSerifAlt" style={{lineHeight: 1.4}}>
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          39
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per user / month
                        </Text>
                      </Stack>

                      <hr className="lp-Pricing-divider" />

                      <Box style={{flex: '1'}}>
                        <Box className="lp-Pricing-plus">
                          <ArrowLeftIcon size={16} />
                          <Text size="200" weight="normal" className="lp-Pricing-plusText">
                            Everything in Copilot, plus:
                          </Text>
                        </Box>

                        <UnorderedList variant="checked" className="lp-Pricing-list">
                          <UnorderedList.Item>Chat personalized to your codebase</UnorderedList.Item>
                          <UnorderedList.Item>Documentation search and summaries</UnorderedList.Item>
                          <UnorderedList.Item>Pull request summaries</UnorderedList.Item>
                          <UnorderedList.Item>Code review skills</UnorderedList.Item>
                          <UnorderedList.Item>
                            Fine-tuned models <sup>4</sup>
                          </UnorderedList.Item>
                        </UnorderedList>
                      </Box>
                      <Button as="a" href="https://github.com/features/preview/copilot-enterprise" variant="primary">
                        Join waitlist
                      </Button>
                    </Stack>
                  </Stack>
                </Grid.Column>
              </Grid>
            </Grid.Column>

            <Grid.Column span={12}>
              <Heading as="h3" size="5" className="lp-Pricing-groupTitle lp-Pricing-groupTitle--individuals">
                For individuals
              </Heading>

              <Grid className="lp-Pricing">
                <Grid.Column
                  span={12}
                  className="lp-Pricing-item has-BlurredBg has-GradientBorder"
                  style={{gap: '16px', backgroundColor: 'rgba(22, 27, 34, 0.85)'}}
                >
                  <Heading as="h4" size="5">
                    Copilot Individual
                  </Heading>

                  <Stack
                    direction={{narrow: 'vertical', regular: 'horizontal'}}
                    gap={24}
                    padding="none"
                    className="lp-Pricing--multiColumn"
                  >
                    <Box style={{flex: '1'}}>
                      <Box marginBlockEnd={32}>
                        <Text as="p" size="200" weight="normal" variant="muted">
                          Code completions, Chat, and more for indie developers and freelancers.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none">
                        <Text size="500" weight="normal" className="is-sansSerifAlt" style={{lineHeight: 1.4}}>
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          10
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per month / <br className="break-whenWide" />
                          $100 per year
                        </Text>
                      </Stack>
                    </Box>

                    <Box style={{flex: '1'}}>
                      <Button as="a" href={copilotSignupPath} block variant="primary">
                        Start a free trial
                      </Button>
                      <Text
                        as="p"
                        size="100"
                        weight="normal"
                        variant="muted"
                        style={{marginTop: 'var(--base-size-32)'}}
                      >
                        Free for verified students, teachers, and maintainers of popular open source projects.
                      </Text>
                    </Box>
                  </Stack>
                </Grid.Column>
              </Grid>
            </Grid.Column>
          </Grid>
        </section>

        <section id="footnotes" className="lp-Section lp-Section--level-1">
          <Grid className="lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <OrderedList>
                <OrderedList.Item className="lp-Footnotes-item">
                  Chat in Visual Studio Code and Visual Studio now generally available. Chat in JetBrains IDEs currently
                  in preview and generally available in 2024. Instructions to enable the preview can be found{' '}
                  <a
                    className="lp-Link--inline"
                    href="https://docs.github.com/copilot/github-copilot-chat/using-github-copilot-chat"
                  >
                    here
                  </a>
                  .
                </OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">Chat in GitHub Mobile is coming soon.</OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">
                  Currently in preview. Instructions to enable Copilot in the CLI in preview can be found{' '}
                  <a className="lp-Link--inline" href="https://docs.github.com/copilot/github-copilot-in-the-cli">
                    here
                  </a>
                  .
                </OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">
                  Currently in preview. Copilot Enterprise grants you access to customization for fine-tuned models
                  (available in 2024).
                </OrderedList.Item>
              </OrderedList>
            </Grid.Column>
          </Grid>
        </section>
      </ThemeProvider>
    </>
  )
}

try{ CopilotPlansIndex.displayName ||= 'CopilotPlansIndex' } catch {}